import { BossKind, Owner, ProjectileFlags, ProjectileKind } from '../constants';
import type { GameWorld } from '../world/GameWorld';

const STAGE_HP_SCALE = 0.35;

export class BossSystem {
  active = false;
  kind = BossKind.None;
  phase = 0;
  phaseTimer = 0;
  attackTimer = 0;
  hp = 0;
  maxHp = 0;
  coreX = 332;
  coreY = 108;
  corePart = -1;
  phaseName = '';
  erasure = 0;
  readonly lineX1 = new Float32Array(8);
  readonly lineY1 = new Float32Array(8);
  readonly lineX2 = new Float32Array(8);
  readonly lineY2 = new Float32Array(8);
  readonly lineTimer = new Float32Array(8);
  readonly lineState = new Uint8Array(8);
  private nextLine = 0;
  private readonly recentDamageAttackIds = new Uint32Array(2048);
  private hpMultiplier = 1;
  private attackIntervalScale = 1;
  private projectileSpeedScale = 1;

  /**
   * Runtime profile boundary. Defaults are legacy-identical; R2 injects the
   * more demanding encounter profile without changing ?r2=0 simulation.
   */
  setTuning(hpMultiplier: number, attackIntervalScale: number, projectileSpeedScale: number): void {
    this.hpMultiplier = Math.max(1, hpMultiplier);
    this.attackIntervalScale = Math.max(.4, Math.min(1, attackIntervalScale));
    this.projectileSpeedScale = Math.max(1, Math.min(1.35, projectileSpeedScale));
  }

  start(kind: BossKind, world: GameWorld): void {
    this.active = true;
    this.kind = kind;
    this.phase = 0;
    this.phaseTimer = 0;
    this.attackTimer = 0;
    this.coreX = 332;
    this.coreY = 108;
    this.corePart = -1;
    this.erasure = 0;
    this.nextLine = 0;
    this.recentDamageAttackIds.fill(0);
    this.lineState.fill(0);
    world.bossParts.reset();
    world.projectiles.clearOwner(Owner.Enemy);
    if (kind === BossKind.CatalogueMouth) {
      this.maxHp = world.difficulty === 0 ? 500 : world.difficulty === 2 ? 760 : 620;
      this.phaseName = 'CLASSIFICATION APERTURE';
      this.corePart = world.bossParts.spawn(1, this.coreX, this.coreY, this.maxHp, 13, false);
      world.bossParts.spawn(0, this.coreX - 18, this.coreY - 30, this.maxHp, 18, false);
      world.bossParts.spawn(0, this.coreX - 18, this.coreY + 30, this.maxHp, 18, false);
      world.bossParts.spawn(1, this.coreX + 12, this.coreY - 24, this.maxHp, 8, true);
      world.bossParts.spawn(1, this.coreX + 12, this.coreY + 24, this.maxHp, 8, true);
    } else if (kind === BossKind.NullScribe) {
      this.maxHp = world.difficulty === 0 ? 650 : world.difficulty === 2 ? 980 : 800;
      this.phaseName = 'DEFERRED INSCRIPTION';
      this.corePart = world.bossParts.spawn(2, this.coreX, this.coreY, this.maxHp, 16, true);
      for (let i = 0; i < 4; i += 1) world.bossParts.spawn(3, this.coreX + 18, this.coreY - 42 + i * 28, this.maxHp, 8, true);
    } else {
      this.maxHp = world.difficulty === 0 ? 1500 : world.difficulty === 2 ? 2400 : 1900;
      this.phaseName = 'HALO';
      this.corePart = world.bossParts.spawn(1, this.coreX, this.coreY, this.maxHp, 15, true);
      for (let i = 0; i < 15; i += 1) world.bossParts.spawn(3, this.coreX, this.coreY, this.maxHp, 9, true);
    }
    this.maxHp = Math.round(this.maxHp * (1 + world.currentStage * STAGE_HP_SCALE) * this.hpMultiplier);
    this.hp = this.maxHp;
    for (let i = 0; i < world.bossParts.indices.active.length; i += 1) {
      if (world.bossParts.indices.active[i] === 0) continue;
      world.bossParts.hp[i] = this.maxHp;
      world.bossParts.maxHp[i] = this.maxHp;
    }
    world.musicLayer = 3;
    world.emitSfx(12);
  }

  update(world: GameWorld): void {
    // HOTPATH-BEGIN
    if (!this.active) return;
    this.phaseTimer += 1;
    this.attackTimer += 1;
    if (this.kind === BossKind.CatalogueMouth) this.updateCatalogue(world);
    else if (this.kind === BossKind.NullScribe) this.updateScribe(world);
    else if (this.kind === BossKind.MnemosyneArray) this.updateMnemosyne(world);
    this.updateLines(world);
    if (this.hp <= 0) this.defeat(world);
    // HOTPATH-END
  }

  private updateCatalogue(world: GameWorld): void {
    const parts = world.bossParts;
    this.coreY = 108 + Math.sin(this.phaseTimer * 0.018) * 18;
    const cycle = this.phaseTimer % 240;
    const open = cycle >= 70 && cycle < 165;
    const aperture = open ? Math.min(38, (cycle - 70) * 0.8, (165 - cycle) * 0.8) : 5;
    for (let i = 0; i < parts.indices.active.length; i += 1) {
      if (parts.indices.active[i] === 0) continue;
      if (i === this.corePart) {
        parts.x[i] = this.coreX;
        parts.y[i] = this.coreY;
        parts.vulnerable[i] = open ? 1 : 0;
      } else if (parts.partType[i] === 0) {
        const upper = i % 2 === 1;
        parts.x[i] = this.coreX - 18;
        parts.y[i] = this.coreY + (upper ? -aperture : aperture);
        parts.angle[i] = upper ? -0.2 : 0.2;
      } else {
        const upper = i % 2 === 1;
        parts.x[i] = this.coreX + 10;
        parts.y[i] = this.coreY + (upper ? -24 : 24);
      }
    }
    if (this.attackTimer >= this.attackInterval(world.difficulty === 2 ? 54 : 72)) {
      this.attackTimer = 0;
      const base = Math.atan2(world.player.y - this.coreY, world.player.x - this.coreX);
      for (let n = -2; n <= 2; n += 1) this.bossShot(world, this.coreX - 8, this.coreY, base + n * 0.17, 1.6, ProjectileKind.EnemyOrb, ProjectileFlags.None);
      if (!open) {
        this.bossShot(world, this.coreX - 16, this.coreY - 18, Math.PI, 2, ProjectileKind.EnemyNeedle, ProjectileFlags.None);
        this.bossShot(world, this.coreX - 16, this.coreY + 18, Math.PI, 2, ProjectileKind.EnemyNeedle, ProjectileFlags.None);
      }
    }
  }

  private updateScribe(world: GameWorld): void {
    const parts = world.bossParts;
    this.coreY = 108 + Math.sin(this.phaseTimer * 0.021) * 52;
    for (let i = 0; i < parts.indices.active.length; i += 1) {
      if (parts.indices.active[i] === 0) continue;
      if (i === this.corePart) {
        parts.x[i] = this.coreX;
        parts.y[i] = this.coreY;
      } else {
        const slot = i - 1;
        parts.x[i] = this.coreX + Math.cos(this.phaseTimer * 0.025 + slot * 1.57) * 26;
        parts.y[i] = this.coreY + Math.sin(this.phaseTimer * 0.025 + slot * 1.57) * 26;
        parts.angle[i] = this.phaseTimer * 0.025 + slot;
      }
    }
    const interval = this.attackInterval(world.difficulty === 2 ? 64 : 82);
    if (this.attackTimer >= interval) {
      this.attackTimer = 0;
      const slot = this.nextLine;
      this.nextLine = (this.nextLine + 1) & 7;
      this.lineX1[slot] = this.coreX - 10;
      this.lineY1[slot] = this.coreY;
      this.lineX2[slot] = 18 + world.rng.nextFloat() * 250;
      this.lineY2[slot] = 18 + world.rng.nextFloat() * 180;
      this.lineTimer[slot] = Math.round(72 * world.difficultySpec.telegraphScale);
      this.lineState[slot] = 1;
      if (world.difficulty === 2 && (slot & 1) === 0) {
        const second = (slot + 4) & 7;
        this.lineX1[second] = this.coreX - 10;
        this.lineY1[second] = this.coreY;
        this.lineX2[second] = 30 + world.rng.nextFloat() * 230;
        this.lineY2[second] = 18 + world.rng.nextFloat() * 180;
        this.lineTimer[second] = Math.round(62 * world.difficultySpec.telegraphScale);
        this.lineState[second] = 1;
      }
    }
  }

  private updateMnemosyne(world: GameWorld): void {
    const ratio = this.hp / this.maxHp;
    const nextPhase = ratio > 0.66 ? 0 : ratio > 0.33 ? 1 : 2;
    if (nextPhase !== this.phase) {
      this.phase = nextPhase;
      this.phaseTimer = 0;
      this.attackTimer = 0;
      this.phaseName = nextPhase === 1 ? 'PRISMATIC VAULT' : 'ERASURE ENGINE';
      world.projectiles.clearOwner(Owner.Enemy);
      world.globalFlashTicks = 12;
      world.emitSfx(13);
    }
    const parts = world.bossParts;
    if (this.phase === 0) {
      this.coreX = 320;
      this.coreY = 108;
      let ring = 0;
      for (let i = 0; i < parts.indices.active.length; i += 1) {
        if (parts.indices.active[i] === 0) continue;
        if (i === this.corePart) { parts.x[i] = this.coreX; parts.y[i] = this.coreY; continue; }
        const angle = this.phaseTimer * 0.012 + ring * (Math.PI * 2 / 15);
        parts.x[i] = this.coreX + Math.cos(angle) * 48;
        parts.y[i] = this.coreY + Math.sin(angle) * 48;
        parts.angle[i] = angle;
        ring += 1;
      }
      if (this.attackTimer >= this.attackInterval(world.difficulty === 2 ? 42 : 58)) {
        this.attackTimer = 0;
        const count = world.difficulty === 0 ? 10 : world.difficulty === 2 ? 18 : 14;
        for (let n = 0; n < count; n += 1) this.bossShot(world, this.coreX, this.coreY, this.phaseTimer * 0.015 + n * (Math.PI * 2 / count), 1.45, ProjectileKind.EnemyOrb, ProjectileFlags.None);
      }
    } else if (this.phase === 1) {
      const gapY = 52 + ((Math.floor(this.phaseTimer / 180) & 1) !== 0 ? 92 : 0);
      let slot = 0;
      for (let i = 0; i < parts.indices.active.length; i += 1) {
        if (parts.indices.active[i] === 0) continue;
        if (i === this.corePart) { parts.x[i] = 344; parts.y[i] = 108; continue; }
        const column = slot % 5;
        const row = Math.floor(slot / 5);
        parts.x[i] = 248 + column * 24;
        const baseY = row === 0 ? 24 : row === 1 ? 108 : 192;
        parts.y[i] = Math.abs(baseY - gapY) < 28 ? baseY + (baseY < gapY ? -34 : 34) : baseY;
        parts.angle[i] = 0;
        slot += 1;
      }
      if (this.attackTimer >= this.attackInterval(world.difficulty === 2 ? 48 : 66)) {
        this.attackTimer = 0;
        this.bossShot(world, 350, gapY - 20, Math.PI, 2.1, ProjectileKind.EnemyNeedle, ProjectileFlags.None);
        this.bossShot(world, 350, gapY + 20, Math.PI, 2.1, ProjectileKind.EnemyNeedle, ProjectileFlags.None);
      }
    } else {
      this.erasure += 0.00055;
      if (this.erasure > 1) this.erasure = 1;
      let slot = 0;
      for (let i = 0; i < parts.indices.active.length; i += 1) {
        if (parts.indices.active[i] === 0) continue;
        if (i === this.corePart) { parts.x[i] = 338; parts.y[i] = 108; continue; }
        const angle = slot * (Math.PI * 2 / 15) - this.phaseTimer * 0.018;
        const radius = 34 + (slot % 3) * 14;
        parts.x[i] = 330 + Math.cos(angle) * radius;
        parts.y[i] = 108 + Math.sin(angle) * radius;
        parts.angle[i] = -angle;
        slot += 1;
      }
      if (this.attackTimer >= this.attackInterval(world.difficulty === 2 ? 34 : 48)) {
        this.attackTimer = 0;
        const y = 18 + world.rng.nextFloat() * 180;
        world.projectiles.spawn(0, y, 0, 0, 2.5, 20, 1, Owner.Enemy, ProjectileKind.Laser, ProjectileFlags.Unblockable | ProjectileFlags.Segment, world.nextAttackId(), 0, 0, 384, y);
        const base = Math.atan2(world.player.y - 108, world.player.x - 338);
        this.bossShot(world, 338, 108, base - 0.24, 1.75, ProjectileKind.EnemyOrb, ProjectileFlags.None);
        this.bossShot(world, 338, 108, base, 1.75, ProjectileKind.EnemyOrb, ProjectileFlags.None);
        this.bossShot(world, 338, 108, base + 0.24, 1.75, ProjectileKind.EnemyOrb, ProjectileFlags.None);
      }
    }
  }

  private updateLines(world: GameWorld): void {
    for (let i = 0; i < this.lineState.length; i += 1) {
      if (this.lineState[i] === 0) continue;
      this.lineTimer[i] -= 1;
      if (this.lineState[i] === 1 && this.lineTimer[i] <= 0) {
        this.lineState[i] = 2;
        this.lineTimer[i] = 24;
        world.projectiles.spawn(this.lineX1[i], this.lineY1[i], 0, 0, 3, 24, 1, Owner.Enemy, ProjectileKind.Laser, ProjectileFlags.Unblockable | ProjectileFlags.Segment, world.nextAttackId(), 0, 0, this.lineX2[i], this.lineY2[i]);
        world.emitSfx(5);
      } else if (this.lineState[i] === 2 && this.lineTimer[i] <= 0) {
        this.lineState[i] = 0;
      }
    }
  }

  damagePart(index: number, damage: number, attackId: number, world: GameWorld): boolean {
    if (!this.active || world.bossParts.indices.active[index] === 0 || world.bossParts.vulnerable[index] === 0) return false;
    if (attackId === 0 || world.bossParts.lastAttackId[index] === attackId) return false;
    const attackSlot = attackId & (this.recentDamageAttackIds.length - 1);
    if (this.recentDamageAttackIds[attackSlot] === attackId) return false;
    this.recentDamageAttackIds[attackSlot] = attackId;
    world.bossParts.lastAttackId[index] = attackId;
    this.hp -= damage;
    if (this.hp < 0) this.hp = 0;
    world.particles.spawn(world.bossParts.x[index], world.bossParts.y[index], -0.4, 0, 18, 2, 2, 2);
    return true;
  }

  applyDeviceDamage(damage: number, world: GameWorld): void {
    if (!this.active) return;
    this.hp -= damage;
    if (this.hp < 0) this.hp = 0;
    world.particles.burst(world.rng, this.coreX, this.coreY, 10, 1.8, 28, 3);
  }

  hpRatio(): number {
    return this.active && this.maxHp > 0 ? this.hp / this.maxHp : 0;
  }

  reset(world: GameWorld): void {
    this.active = false;
    this.kind = BossKind.None;
    this.phase = 0;
    this.phaseTimer = 0;
    this.hp = 0;
    this.maxHp = 0;
    this.phaseName = '';
    this.erasure = 0;
    this.recentDamageAttackIds.fill(0);
    this.lineState.fill(0);
    world.bossParts.reset();
  }

  private bossShot(world: GameWorld, x: number, y: number, angle: number, speed: number, kind: ProjectileKind, flags: ProjectileFlags): void {
    const scaled = speed * world.difficultySpec.projectileSpeed * this.projectileSpeedScale;
    world.projectiles.spawn(x, y, Math.cos(angle) * scaled, Math.sin(angle) * scaled, 2.6, 260, 1, Owner.Enemy, kind, flags, world.nextAttackId());
  }

  private attackInterval(baseTicks: number): number {
    return Math.max(18, Math.round(baseTicks * this.attackIntervalScale));
  }

  private defeat(world: GameWorld): void {
    this.active = false;
    this.kind = BossKind.None;
    world.projectiles.clearOwner(Owner.Enemy);
    world.particles.burst(world.rng, this.coreX, this.coreY, 48, 3.8, 70, 4);
    world.globalFlashTicks = 20;
    world.screenShakeTicks = 24;
    world.emitSfx(14);
    world.onBossDefeated();
    world.bossParts.reset();
    this.lineState.fill(0);
  }
}
